import SimplexNoise from 'simplex-noise';
import {sortPts} from './sort.js';
import {joinSegments, contour, triangulate} from './triangulate.js';

window.rasterTimes = [];

export const getTrianglePoints = (pts, step=6, percentage=50) => {
    let rasterized = rasterize(pts, step, percentage);
    let noise = new SimplexNoise();
    let _pts = [];
    let maxDist = -1;
    for (let pt of rasterized) {
        pt.x = Math.round(pt.x);
        pt.y = Math.round(pt.y);
        let value = noise.noise2D(pt.x, pt.y);
        let dist = (value + 1)/2;
        /*
        let {x,y} = pt;
        let dist = 1/(2+Math.sin(2*Math.sqrt(x*x + y*y))) * (.75+.5*Math.sin(x*2));
        */
        pt.dist =dist;
        if (Math.random() < 1) {
            dist = Math.min(pt.distX, pt.distY)/step;
            pt.dist = dist;
        }
        if (dist > maxDist) {
            maxDist = dist;
        }
        _pts.push(pt);
    }

    let triangles = triangulate(_pts, step);
    /*
    let segments = [
        ...joinSegments(contour(triancles, 0.1)),
        ...joinSegments(contour(triangles, 0.2)),
        ...joinSegments(contour(triangles, 0.3)),
        ...joinSegments(contour(triangles, 0.4)),
        ...joinSegments(contour(triangles, 0.5)),
        ...joinSegments(contour(triangles, 0.6)),
        ...joinSegments(contour(triangles, 0.7)),
        ...joinSegments(contour(triangles, 0.8)),
        ...joinSegments(contour(triangles, 0.9)),
        ...joinSegments(contour(triangles, 1)),
    ];
    */
        
    let segments= [];
    let contours = [];
    for (let i=2; i < Math.max(8,maxDist); i+=1) {
        let seg = contour(triangles, i);
        segments = [... segments, ...seg];
        contours = [... contours, ... joinSegments(seg)];
    }

    return {
        triangles,
        points: _pts, 
        segments : segments,
        contours : segments
    };
};

export const rasterize = (pts, step=16, percentage=50) => {
    let startTime = new Date().getTime();
    pts = sortPts(pts);

    let [Ax,Ay,Bx,By,Cx,Cy] = pts;

    if (By === Cy) {
        // bottom is flat
        let ret = fillBottomFlatTriangle(pts, step, percentage);
        return ret;

    } else if (Ay === By) {
        // top is flat
        let ret = fillTopFlatTriangle(pts, step, percentage);
        return ret;

    } else {
        let Dx = Math.round(Ax + (By - Ay) / (Cy - Ay) * (Cx - Ax));
        let Dy =  Math.round(By);
        let tri1 = [Bx,By, Dx,Dy, Cx, Cy];
        tri1 = sortPts(tri1);
        let filledTop = fillTopFlatTriangle(tri1, step, true, Ay);
        let lastY = filledTop[filledTop.length - 1];
        let [x1, x2] = ptSlope(Ax,Ay,Bx,By,Dx,Dy, lastY);
        Bx =  x1;
        By = lastY;
        Dx = x2;
        Dy = lastY;
        let tri2 = [Ax,Ay,Bx,By,Dx,Dy];
        tri2 = sortPts(tri2);
        let startX = getMinXWithY(filledTop, lastY); 
        let ret = [
            ... filterOutY(filledTop, lastY), 
            ... fillBottomFlatTriangle(tri2, step, startX, Cy)
        ];
        return ret;
 
    }
    
    return [];
};

const ptSlope = (Ax,Ay,Bx,By,Dx,Dy, y) => {
    let slope1 = (Ay - By) / (Ax - Bx);
    let x1 = Ax - (Ay - y)/slope1;

    let slope2 = (Ay - Dy) / (Ax - Dx);
    let x2 = Ax - (Ay - y)/slope2;

    return [x1,x2];
};

const fillBottomFlatTriangle = ([v1x, v1y, v2x, v2y, v3x, v3y], step, startX, startY) => {
    let invslope1 = (v2x - v1x) / (v2y - v1y);
    let invslope2 = (v3x - v1x) / (v3y - v1y);
   
    let curx1 = v2x;
    let curx2 = v3x;
    
    let itt=0;
    let pts = [];

    startX = pixelate(startX || curx1, step) ;
    startY = pixelate(startY || v2y);


    for (let scanlineY = v2y; scanlineY > v1y; scanlineY--) {
        let dist_end = Math.abs(v1y - scanlineY);
        let dist_start  = Math.abs(scanlineY - v2y);

        if (itt%step === 0) {
            let _pts = line(curx1, scanlineY, curx2, scanlineY, step, startX, startY);
            /*
            _pts.forEach(
                pt => {
                    pt.distY = Math.min(dist_end,dist_start);
                });
                */
                    
            pts = [... pts, ..._pts];
        }
        curx1 -= invslope1;
        curx2 -= invslope2;
        itt++;
        if (itt > 10000) {
            break;
        }
    }
    return pts;
};

const fillTopFlatTriangle = ([v1x, v1y, v2x, v2y, v3x, v3y], step, isPart=false, startY) => {
    let invslope1 = (v3x - v1x) / (v3y - v1y);
    let invslope2 = (v3x - v2x) / (v3y - v2y);
    
    let curx1 = v3x;
    let curx2 = v3x;
   
    let pts = [];
    let itt=0;

    let start_curx1 = pixelate(curx1, step);
    let start_scanY = pixelate(v3y, step);
    let scanlineY = pixelate(v3y, step);
    let flag=true;
    for (; flag; scanlineY--) {
        let dist_end = Math.abs(startY - scanlineY);
        let dist_start  = Math.abs(v3y - scanlineY);

        if (itt%step===0) {
            if (scanlineY <= v1y) {
                if (isPart) {
                    flag = false;
                } else {
                    break;
                }
            }
            let _pts = line(curx1, scanlineY, curx2, scanlineY, step, start_curx1, start_scanY);
            /*
            _pts.forEach(
                pt => {
                    pt.distY = Math.min(dist_end, dist_start);
                });
                */
            pts = [... pts, ... _pts];
        }
        curx1 -= invslope1;
        curx2 -= invslope2;
        itt++;

        if (itt > 10000) {
            break;
        }
    }
    return pts;
};

const line = (x0, y0, x1, y1, step=4, startX, startY) => {
    let _x0 = x0;
    let _y0 = y0;

    while (startX > x0) {
        startX -= step;
    }
    x0 = Math.round(startX);
    y0 = Math.round(y0);
    
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    var dx = Math.abs(x1 - x0);
    var dy = Math.abs(y1 - y0);
    var sx = x0 < x1 ? 1: -1;
    var sy = y0 < y1 ? 1 : -1;
    var err = dx - dy;
    
    let pts = [];
    let itt=0;
    while (true) {
        if (x0 == x1 && y0 == y1) break;

        if (itt%step===0 && x0 > _x0) {
            let start_dist = Math.abs(x0 - startX);
            let end_dist = Math.abs(x1 - x0);
            pts.push(x0);
            pts.push(y0);
            //pts.push({x: x0, y: y0, distX: Math.min(start_dist, end_dist)});
        }
        itt++;
        var e2 = 2 * err;
        if (e2 > -dy) {
            err -= dy;
            x0 += sx;
        }
        if (e2 < dx) {
            err += dx;
            y0 += sy;
        }

        if (itt > 10000) {
            break;
        }
    }
    return pts;
};


const getMinXWithY = (pts, lastY) => {
    let minX = 10000;
    for (let i=0 ; i < pts.length; i+=2) {
        if (pts[i+1] === lastY) {
            if (pts[i] < minX) {
                minX = pts[i];
            }
        }
    }
    return minX;
};

const filterOutY = (pts, lastY) => {
    //filledTop.filter(pt => pt.y !== lastY),

    let _pts = [];
    for (let i=0 ; i < pts.length; i+=2) {
        if (pts[i+1] !== lastY) {
            _pts.push(pts[i]);
            _pts.push(pts[i+1]);
        }
    }
    return _pts;
}

const pixelate = (x, step) => {
    return x - (x % step);
};
